import { FormattedText, useConfirmationDialog } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";
import { resolveFormattedTextString } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { CommunityJoinabilityKind, NonMemberJoinabilityKind } from "./common";

export type LeaveCommunityConfirmationData = {
	name: string;
	joinabilityKindAfterLeaving: NonMemberJoinabilityKind;
	hasRejoinHint: boolean;
};

export function useLeaveCommunityConfirmationDialog(): [
	element: ReactElement | null,
	open: (data: LeaveCommunityConfirmationData) => Promise<TryResult<void>>
] {
	const [element, openConfirmationDialog] = useConfirmationDialog();

	const open = useCallback(
		(data: LeaveCommunityConfirmationData): Promise<TryResult<void>> => {
			const {
				name,
				joinabilityKindAfterLeaving,
				hasRejoinHint
			} = data;

			switch (joinabilityKindAfterLeaving) {
				case CommunityJoinabilityKind.CannotJoin:
					return openConfirmationDialog({
						trackingId: "leave-community-confirmation-dialog",
						title: resolveFormattedTextString({
							template: localize("_AreYouSureYouWantToLeaveTheCommunityName"),
							replacements: [
								{
									what: "{{Name}}",
									filler: name
								}
							]
						}),
						confirmText: localize("_Leave"),
						children: (
							<FormattedText
								text={localize("_LeaveHiddenCommunityConfirmationDialogDescription")}
								thingsToWrap={[
									{
										blockId: "Note",
										wrap: (x) => <b>{x}</b>
									}
								]}
							/>
						)
					});
				case CommunityJoinabilityKind.CanRequestToJoin:
					return openConfirmationDialog({
						trackingId: "leave-community-confirmation-dialog",
						title: localize("_AreYouSureYouWantToLeaveThisCommunity"),
						confirmText: localize("_Leave"),
						children: localize("_YouWillHaveToRequestToJoinAgain")
					});
				case CommunityJoinabilityKind.CanJoin:
					return openConfirmationDialog({
						trackingId: "leave-community-confirmation-dialog",
						title: localize("_AreYouSureYouWantToLeaveThisCommunity"),
						confirmText: localize("_Leave"),
						children: hasRejoinHint
							? localize("_YouCanBecomeAMemberAgainViaTheDiscoverCommunitiesPage")
							: undefined
					});
				default:
					never(joinabilityKindAfterLeaving);
			}
		},
		[openConfirmationDialog]
	);

	return [element, open];
}
